import { AlmanacConfig } from "protos/almanac/almanac";
import { localeCompare } from "portal/utils/strings";
import { portalApi } from "portal/state/portalApi";
import { useMemo } from "react";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";

export type AlmanacGroup = {
  label: string;
  isCarbon?: boolean;
  almanacs: AlmanacConfig[];
};

/**
 * Gets customer-wide almanac options (including carbon)
 * Groups by robot.
 * Returns a default almanac id that is the first carbon almanac result (likely the Carbon default)
 */
export const useAlmanacOptions = (
  customerUuid?: string
): {
  almanacGroups: AlmanacGroup[];
  defaultAlmanacId: string;
  isLoading: boolean;
} => {
  const { t, i18n } = useTranslation();

  const { data: globalAlmanacs, isLoading: isLoadingGlobal } = useQueryPopups(
    portalApi.endpoints.listGlobalAlmanacs.useQuery({}),
    { errorVariant: "warning" }
  );
  const { data: customerRobotAlmanacs, isLoading: isLoadingCustomer } =
    useQueryPopups(
      portalApi.endpoints.listCustomerAlmanacsByCustomer.useQuery(
        { customerUuid },
        { skip: !customerUuid }
      ),
      { errorVariant: "warning" }
    );

  const { almanacGroups, defaultAlmanacId } = useMemo(() => {
    const groups: AlmanacGroup[] = [];

    const sortedGlobalAlmanacs = [...(globalAlmanacs ?? [])].sort((a, b) =>
      localeCompare(a.name, b.name, i18n.language)
    );
    if (sortedGlobalAlmanacs.length > 0) {
      groups.push({
        label: t("components.categoryTemplates.fields.carbonAlmanacs"),
        isCarbon: true,
        almanacs: sortedGlobalAlmanacs,
      });
    }

    const robotGroups = [...(customerRobotAlmanacs ?? [])].sort((a, b) =>
      localeCompare(a.serial, b.serial, i18n.language)
    );
    for (const robotGroup of robotGroups) {
      const almanacs = [...robotGroup.almanacs].sort((a, b) =>
        localeCompare(a.name, b.name, i18n.language)
      );
      if (almanacs.length === 0) {
        continue;
      }
      groups.push({
        label: robotGroup.serial,
        almanacs,
      });
    }

    return {
      almanacGroups: groups,
      defaultAlmanacId: groups[0]?.almanacs[0]?.id ?? "",
    };
  }, [globalAlmanacs, i18n.language, customerRobotAlmanacs, t]);

  return {
    almanacGroups,
    defaultAlmanacId,
    isLoading: isLoadingGlobal || isLoadingCustomer,
  };
};
